// frontend/src/components/profile/SupportTicketList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supportApi } from '../../api/support';
import CreateTicketModal from './CreateTicketModal';
import { FaPlus, FaTicketAlt, FaCircle, FaChevronRight, FaInfoCircle } from 'react-icons/fa';

function SupportTicketList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await supportApi.getMyTickets();
      setTickets(data);
    } catch (err) {
      console.error("Failed to load tickets", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    switch (status) {
      case 'open': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'awaiting_customer': return 'text-orange-600 bg-orange-100';
      case 'resolved': return 'text-gray-600 bg-gray-100';
      case 'closed': return 'text-gray-500 bg-gray-200';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div>
      {/* UPDATED HEADER: Stack on mobile, Row on desktop */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaTicketAlt className="text-primary" /> Support Tickets
        </h2>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition duration-300 w-full sm:w-auto"
        >
          <FaPlus /> Create Ticket
        </button>
      </div>

      {/* --- TOOLKIT --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
              <h4 className="font-bold text-blue-800 text-sm">Need Help?</h4>
              <p className="text-sm text-blue-700">
                  Create a ticket for order issues, payment problems, or general inquiries. You can link specific orders or bookings to your ticket for faster resolution.
              </p>
          </div>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">You have no support tickets.</p>
          <button onClick={() => setIsModalOpen(true)} className="text-primary hover:underline">
            Open a new ticket
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map(ticket => (
            <Link 
              key={ticket.id} 
              to={`/account/support/${ticket.id}`}
              className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-xs text-gray-500 font-bold">{ticket.ticket_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                      <FaCircle size={6} /> {ticket.status ? ticket.status.replace('_', ' ') : 'Unknown'}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg">{ticket.subject}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Category: {ticket.category} • Last Updated: {new Date(ticket.last_updated_at).toLocaleDateString()}
                  </p>
                </div>
                <FaChevronRight className="text-gray-300" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {isModalOpen && (
        <CreateTicketModal 
          onClose={() => setIsModalOpen(false)} 
          onTicketCreated={fetchTickets} 
        />
      )}
    </div>
  );
}

export default SupportTicketList;